import { currency, percentage } from '@sunbit-dev/common/helpers/numbers';
import * as React from 'react';
import styled, { css } from 'styled-components';
import SStrong from '../SStrong/SStrong';
import SunbitDynamicIcon from '../sunbitDynamicIcon/SunbitDynamicIcon';
import scrollRight from '../../assets/scroll-right.svg';
import scrollLeft from '../../assets/scroll-left.svg';
import { Shimmer } from '../shimmerAnimation/ShimmerAnimation';
import { OnlineThemeType } from '../../themes/online';
import { PaymentOptionCard } from './PaymentOptionCard';
import { useMemo } from 'react';

const SPaymentOptions = styled.div(
  ({ theme, isLoading }: { isLoading: boolean; theme: OnlineThemeType }) => css`
    font-family: Poppins;
    font-size: 14px;
    font-weight: ${theme.font.weight.normal};
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-top: ${!isLoading ? '20px !important' : ''};

    // Bad definition
    width: 980px;
    position: relative;
    margin: auto;
  `
);

const SOptions = styled.div(({ optionsNum, breakingSizeQuery }: { optionsNum: number; breakingSizeQuery?: string }) => {
  return css`
    display: flex;
    flex-direction: column-reverse;
    justify-content: center;
    align-items: center;
    width: 100%;
    padding: 0;
    overflow: hidden;

    ${() => {
      // In other words special treatment to Epay app
      // should be fixed in https://sunbit.atlassian.net/browse/SBT-6897
      if (breakingSizeQuery)
        return css`
          @media only screen and ${breakingSizeQuery} {
            flex-direction: row-reverse;
            padding: 20px 0;

            justify-content: ${optionsNum > 2 ? 'flex-start' : 'center'};
            align-items: center;

            ${SScroll} {
              display: flex;
            }
          }
        `;

      return css`
        // Should change to more generic behavior
        @media only screen and (min-width: 1320px) {
          flex-direction: row-reverse;
          justify-content: ${optionsNum > 2 ? 'flex-start' : 'center'};
          align-items: center;

          // Bad definition
          width: 870px;
          padding-bottom: 20px;

          ${SScroll} {
            display: flex;
          }
        }
      `;
    }};
  `;
});

const SPaymentOption = styled.div<{ index: number }>(
  ({ index }) => `
  margin: 10px;
  scroll-margin: 10px;
  z-index: ${index};
  flex: 1 1 0;
`
);

const SLoadingAnimationWrapper = styled.div(
  () => `
  width: 100%;
  min-width: 311px;
  display: flex;
  flex-direction: column;
  gap: 30px;
  align-items: center;
  pointer-events: none;
`
);

const SLineLoader1 = styled.div(
  ({ theme }) => css`
    overflow: hidden;
    align-items: center;
    color: ${theme.colors.disabledText};
    font-family: Poppins;
    text-align: center;
    padding: 0;
    font-size: 18px;
    font-weight: 700;
    line-height: 1.19;
  `
);

const SLineLoader2 = styled.div(
  ({ theme }) => `
  overflow: hidden;
  background-color: ${theme.colors.grayWhite};
  height: 20px;
  border-radius: 5px;
  width: 311px;
`
);

const SPaymentOptionDataSectionNumbers = styled.section(
  ({ theme }) => css`
    display: flex;
    flex-grow: 0.4;
    margin-top: 5px;
    overflow: hidden;
    transition: all 300ms;
    justify-content: space-between;
    font-size: ${theme.font.size.small};
    font-weight: ${theme.font.weight.light};
    height: 55px;
    opacity: 1;
    color: inherit;
  `
);

const SPaymentOptionDataNumbersColumn = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-start;
  line-height: 1.4;
  height: 35px;

  > .total-amount-text {
    text-align: right;
  }
`;

const SShimmerLines = styled.div(() => {
  return `
    display: flex;
    flex-direction: column;
    gap: 19px;
    width: 311px;
  `;
});

const SPaymentOptionInterestColumn = styled.div(({ theme }) => {
  return `
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: flex-start;
    line-height: 1.4;
    height: 35px;
    @media only screen and ${theme.device.tablet} {
      margin-right: 0;
    }
  `;
});

const SScrollLeft = styled.img<{ scrolled: boolean }>(
  ({ scrolled }) => `
  visibility: ${scrolled ? 'visible' : 'hidden'};
  left: -10px;
`
);

const SScrollRight = styled.img<{ scrolled: boolean }>(
  ({ scrolled }) => `
  visibility: ${!scrolled ? 'visible' : 'hidden'};
  right: -10px;
`
);

const SScroll = styled.div(
  () => `
  width: 100%;
  height: 100%;
  position: absolute;
  display: none;
  justify-content: space-between;
  align-items: center;
  pointer-events: none;
  z-index: 99;
  left: 0;

  > img {
    width: 24px;
    height: 24px;
    pointer-events: all;
    cursor: pointer;
    position: absolute;
  }
`
);

// tslint:enable:variable-name
// ############################ End Styles ###########################

export type SelectOption = {
  amount: number;
  numOfPayments: number;
  effectiveApr: number;
  financialCharge: number;
  totalDownPayment: number;
  amountFinanced: number;
  expanded: boolean;
};

export type PaymentOptionsOnChange = (value: number) => void;

export interface IPaymentOptionsProps {
  onChange: PaymentOptionsOnChange;
  options: SelectOption[];
  selectedOption?: number;
  className?: string;
  amount: number;
  isLoading: boolean;
  hiddenMode: boolean;
  isExpandable?: boolean;
  breakingSizeQuery?: string;
  alwaysOpenCards?: boolean;
}

export interface OptionItemProps {
  index: number;
  scrollToStart?: () => void;
  scrollToEnd?: () => void;
  scrolled?: boolean;
  item: SelectOption;
  firstOptionRef?: React.RefObject<HTMLDivElement>;
  lastOptionRef?: React.RefObject<HTMLDivElement>;
  selectedCardRef?: React.RefObject<HTMLDivElement>;
  expandedContent: (item: SelectOption) => JSX.Element;
  numOfOptions?: number;
  isSelected: boolean;
  handleSelect: PaymentOptionsOnChange;
  isExpandable: boolean;
  alwaysOpenCards?: boolean;
  breakingSizeQuery?: string;
}

// To support zero currency
const formatter = { format: { zero: '%s%v' } };

export const OptionItem = ({
  index,
  scrollToStart,
  scrollToEnd,
  scrolled,
  item,
  firstOptionRef,
  lastOptionRef,
  selectedCardRef,
  expandedContent,
  numOfOptions,
  isSelected,
  handleSelect,
  isExpandable,
  alwaysOpenCards,
  breakingSizeQuery,
}: OptionItemProps) => {
  const onSelect = React.useCallback(
    (e) => {
      handleSelect(index);
    },
    [handleSelect, index]
  );

  return (
    <SPaymentOption
      className="payment-option"
      data-at={`planCard${index}`}
      {...{
        index,
        ref: index === 0 ? firstOptionRef : index === numOfOptions ? lastOptionRef : null,
      }}
    >
      <PaymentOptionCard
        {...{
          className: 'payment-option-card',
          numOfPayments: item.numOfPayments,
          ref: isSelected ? selectedCardRef : null,
          paymentAmount: currency(item.amount),
          expandedContent: expandedContent(item),
          isSelected,
          handleSelect: onSelect,
          isExpandable,
          alwaysOpenCards,
          breakingSizeQuery,
        }}
      />
    </SPaymentOption>
  );
};

// tslint:disable-next-line: variable-name
const PaymentOptions: React.FunctionComponent<IPaymentOptionsProps> = ({
  className,
  isExpandable,
  selectedOption,
  options,
  onChange,
  hiddenMode,
  isLoading,
  breakingSizeQuery,
  // Remove after redesign
  alwaysOpenCards,
}) => {
  const [scrolled, setScrolled] = React.useState(false);

  const firstOptionRef = React.useRef(null);
  const lastOptionRef = React.useRef(null);
  const selectedCardRef = React.useRef(null);

  /** reversed order of plans: (if changed switch the functions): **/

  const scrollToStart = React.useCallback(() => {
    lastOptionRef.current?.scrollIntoView({ behavior: 'smooth' });
    setScrolled(false);
  }, [lastOptionRef.current, setScrolled]);

  const scrollToEnd = React.useCallback(() => {
    firstOptionRef.current?.scrollIntoView({ behavior: 'smooth' });
    setScrolled(true);
  }, [firstOptionRef.current, setScrolled]);

  const isInViewport = (element): boolean => {
    if (!element) return false;

    const rect = element.getBoundingClientRect();
    return (
      rect.top >= 0 &&
      rect.left >= 0 &&
      rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
      rect.right <= (window.innerWidth || document.documentElement.clientWidth)
    );
  };

  React.useEffect(() => {
    if (isLoading || selectedOption === -1) return;
    lastOptionRef.current?.scrollIntoView();
    if (selectedCardRef.current && !isInViewport(selectedCardRef.current)) {
      setTimeout(() => {
        selectedCardRef.current.scrollIntoView();
      }, 0);
      setScrolled((prevState) => !prevState);
    }
  }, [isLoading]);

  const loaderContent = (
    <>
      <SunbitDynamicIcon cardIndex={999} dataAt="sunbitDynamicIcon" flavor={'transparent'} number={0} />
      <h1>$0/mo</h1>
    </>
  );

  const expandedContent = (option: SelectOption) => {
    const {
      effectiveApr: _effectiveApr,
      numOfPayments,
      financialCharge,
      amountFinanced,
      totalDownPayment,
    } = option || {};

    const effectiveApr = _effectiveApr >= 0 ? _effectiveApr : 0;

    return (
      <>
        <SPaymentOptionDataSectionNumbers>
          <SPaymentOptionDataNumbersColumn data-at="effectiveApr">
            <span className="title">APR</span>
            <div className="amount-txt">
              <SStrong data-at="aprAmountTxt">{percentage(effectiveApr)}</SStrong>
            </div>
          </SPaymentOptionDataNumbersColumn>

          <SPaymentOptionInterestColumn data-at="financeCharges">
            <span className="title">Interest</span>
            <div className="amount-txt">
              <SStrong data-at="finChargeAmountTxt">{currency(financialCharge, formatter)}</SStrong>(
              {currency(financialCharge ? financialCharge / numOfPayments : financialCharge, formatter)} avg/mo)
            </div>
          </SPaymentOptionInterestColumn>

          <SPaymentOptionDataNumbersColumn data-at="totalOfPayments">
            <span className="title">Total Cost</span>
            <div className="amount-txt">
              <SStrong className="total-amount-text" data-at="totalAmountTxt">
                {currency(financialCharge + amountFinanced + totalDownPayment)}
              </SStrong>
            </div>
          </SPaymentOptionDataNumbersColumn>
        </SPaymentOptionDataSectionNumbers>
      </>
    );
  };

  const content = isLoading ? (
    <SLoadingAnimationWrapper>
      <SShimmerLines>
        <SLineLoader1>Choose a payment plan</SLineLoader1>
        <SLineLoader2>
          <Shimmer className="Shimmer" />
        </SLineLoader2>
      </SShimmerLines>
      <SOptions className="option" breakingSizeQuery={breakingSizeQuery} optionsNum={options?.length || 0}>
        {options?.map((item, index) => (
          <SPaymentOption key={`loading-${index}`} index={index}>
            <PaymentOptionCard isLoading loaderContent={loaderContent} data-at={`planCard${index}`} />
          </SPaymentOption>
        ))}
      </SOptions>
    </SLoadingAnimationWrapper>
  ) : hiddenMode ? null : (
    <SOptions className="option" breakingSizeQuery={breakingSizeQuery} optionsNum={options?.length || 0}>
      {options?.length > 3 && (
        <SScroll>
          <SScrollLeft src={scrollLeft} scrolled={scrolled} onClick={scrollToStart} />
          <SScrollRight src={scrollRight} scrolled={scrolled} onClick={scrollToEnd} />
        </SScroll>
      )}

      {options?.map((item, index) => (
        <OptionItem
          key={'planCard-' + index}
          {...{
            index,
            scrollToStart,
            scrolled,
            scrollToEnd,
            item,
            firstOptionRef,
            lastOptionRef,
            selectedCardRef,
            expandedContent,
            handleSelect: onChange,
            isSelected: selectedOption === index,
            numOfOptions: options?.length - 1,
            isExpandable,
            alwaysOpenCards,
            breakingSizeQuery,
          }}
        />
      ))}
    </SOptions>
  );

  return (
    <SPaymentOptions {...{ className, isLoading, role: 'radiogroup', 'data-at': 'paymentPlansWrap' }}>
      {content}
    </SPaymentOptions>
  );
};

export default PaymentOptions;
